import { create } from 'zustand'

import { fetchUserSkinsApi } from '@api'

import { createSelectors, Mapper } from './helpers'

export enum SkinType {
  AVATAR = 'avatar',
  AVATAR_EFFECT = 'avatarEffect',
  SPIRAL = 'spiral',
  STAR = 'star',
}

export interface SkinItem {
  id: string
  name: string
  isAvailable: boolean
  isSelected: boolean
  appFiles?: string
  iconUrl?: string
}

export interface SkinItemBackend {
  id: string
  name: string
  is_available: boolean
  is_selected: boolean
  app_files?: string
  icon_url?: string
}

export interface UserSkinsResponse {
  avatarEffects: SkinItem[]
  avatars: SkinItem[]
  spiralEffects: SkinItem[]
  starEffects: SkinItem[]
}

export interface UserSkinsResponseBackend {
  avatar_effects: SkinItemBackend[]
  avatars: SkinItemBackend[]
  spiral_effects: SkinItemBackend[]
  star_effects: SkinItemBackend[]
}

const SkinItemMapper: Mapper<SkinItemBackend, SkinItem> = {
  mapFromBackend: function (data) {
    return {
      id: data.id,
      name: data.name,
      isAvailable: data.is_available,
      isSelected: data.is_selected,
      appFiles: data.app_files,
      iconUrl: data.icon_url,
    }
  },
  mapToBackend(data) {
    return {
      id: data.id,
      name: data.name,
      is_available: data.isAvailable,
      is_selected: data.isSelected,
      app_files: data.appFiles,
      icon_url: data.iconUrl,
    }
  },
}

const UserSkinsResponseMapper: Mapper<
  UserSkinsResponseBackend,
  UserSkinsResponse
> = {
  mapFromBackend: function (data) {
    return {
      avatarEffects: data.avatar_effects.map(SkinItemMapper.mapFromBackend),
      avatars: data.avatars.map(SkinItemMapper.mapFromBackend),
      spiralEffects: data.spiral_effects.map(SkinItemMapper.mapFromBackend),
      starEffects: data.star_effects.map(SkinItemMapper.mapFromBackend),
    }
  },
  mapToBackend(data) {
    return {
      avatar_effects: data.avatarEffects.map(SkinItemMapper.mapToBackend),
      avatars: data.avatars.map(SkinItemMapper.mapToBackend),
      spiral_effects: data.spiralEffects.map(SkinItemMapper.mapToBackend),
      star_effects: data.starEffects.map(SkinItemMapper.mapToBackend),
    }
  },
}

interface SkinsState {
  skins: UserSkinsResponse | null
  isSkinsFetching: boolean
}

export interface UserCardsResponseBackend {
  count: number
  description: string
  icon: string
  id: string
  last_collected_at: string
  name: string
}

interface SkinsActions {
  actions: {
    setSkins: (skins: UserSkinsResponse) => void
    fetchSkins: () => Promise<void>
    selectSkin: (type: SkinType, skinId: string) => void
  }
}

const useSkinsStoreBase = create<SkinsState & SkinsActions>((set, get) => ({
  skins: null,
  isSkinsFetching: false,
  actions: {
    setSkins: (skins) => {
      set({ skins })
    },
    fetchSkins: async () => {
      set({ isSkinsFetching: true })
      try {
        const { data, ok } = await fetchUserSkinsApi()
        if (!ok || !data) {
          set({ isSkinsFetching: false })
          return
        }
        const skins = UserSkinsResponseMapper.mapFromBackend(data)
        set({ skins, isSkinsFetching: false })
      } catch (error) {
        set({ isSkinsFetching: false })
        console.error('Error fetching skins:', error)
      }
    },
    selectSkin: (type, skinId) => {
      const currentSkins = get().skins
      if (!currentSkins) return

      const updateSkinSelection = (skins: SkinItem[]) =>
        skins.map((skin) => ({
          ...skin,
          isSelected: skin.id === skinId,
        }))

      let updatedSkins: UserSkinsResponse

      switch (type) {
        case SkinType.AVATAR:
          updatedSkins = {
            ...currentSkins,
            avatars: updateSkinSelection(currentSkins.avatars),
          }
          break
        case SkinType.AVATAR_EFFECT:
          updatedSkins = {
            ...currentSkins,
            avatarEffects: updateSkinSelection(currentSkins.avatarEffects),
          }
          break
        case SkinType.SPIRAL:
          updatedSkins = {
            ...currentSkins,
            spiralEffects: updateSkinSelection(currentSkins.spiralEffects),
          }
          break
        case SkinType.STAR:
          updatedSkins = {
            ...currentSkins,
            starEffects: updateSkinSelection(currentSkins.starEffects),
          }
          break
        default:
          return
      }

      set({ skins: updatedSkins })
    },
  },
}))

export const useSkinsActions = () => useSkinsStoreBase((state) => state.actions)
export const useSkins = () => useSkinsStoreBase((state) => state.skins)
export const useIsSkinsFetching = () =>
  useSkinsStoreBase((state) => state.isSkinsFetching)

export const useAvatarSkins = () =>
  useSkinsStoreBase((state) => state.skins?.avatars || [])
export const useAvatarEffectSkins = () =>
  useSkinsStoreBase((state) => state.skins?.avatarEffects || [])
export const useSpiralEffectSkins = () =>
  useSkinsStoreBase((state) => state.skins?.spiralEffects || [])
export const useStarEffectSkins = () =>
  useSkinsStoreBase((state) => state.skins?.starEffects || [])

export const useSelectedAvatar = () =>
  useSkinsStoreBase(
    (state) => state.skins?.avatars.find((skin) => skin.isSelected) || null
  )
export const useSelectedAvatarEffect = () =>
  useSkinsStoreBase(
    (state) =>
      state.skins?.avatarEffects.find((skin) => skin.isSelected) || null
  )
export const useSelectedSpiralEffect = () =>
  useSkinsStoreBase(
    (state) =>
      state.skins?.spiralEffects.find((skin) => skin.isSelected) || null
  )
export const useSelectedStarEffect = () =>
  useSkinsStoreBase(
    (state) => state.skins?.starEffects.find((skin) => skin.isSelected) || null
  )

export const useSkinsStore = createSelectors(useSkinsStoreBase)

export { SkinItemMapper, UserSkinsResponseMapper }
